import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    TablePagination
} from '@mui/material';
import { getFirestore, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { toast } from 'react-toastify';
import moment from 'moment';
import Takkevariheader from './Takkevariheader';

const db = getFirestore();

function Amountgivenreport() {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dates, setDates] = useState({
        startDate: '',
        endDate: '',
    });
    const [customers, setCustomers] = useState({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleDateChange = (e) => {
        setDates({
            ...dates,
            [e.target.name]: e.target.value,
        });
    };

    const fetchCustomers = async () => {
        const customersCollection = collection(db, 'takkevaricusts');
        const customerSnapshot = await getDocs(customersCollection);
        const customersMap = {};
        customerSnapshot.docs.forEach(doc => {
            customersMap[doc.id] = doc.data().name;
        });
        setCustomers(customersMap);
        return customersMap;
    };

    const handleGenerateReport = async () => {
        if (!dates.startDate || !dates.endDate) {
            toast.error("Please select both a start and end date.");
            return;
        }

        setLoading(true);
        setReportData([]); // Clear previous data
        
        try {
            const customerMap = await fetchCustomers();

            const startDateObj = moment(dates.startDate).startOf('day').toDate();
            const endDateObj = moment(dates.endDate).endOf('day').toDate();

            const loansRef = collection(db, 'takkevariloans');
            const q = query(
                loansRef,
                where('loandate', '>=', startDateObj),
                where('loandate', '<=', endDateObj),
                orderBy('loandate', 'asc')
            );

            const querySnapshot = await getDocs(q);
            const data = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                customerName: customerMap[doc.data().customerId] || 'N/A'
            }));

            setReportData(data);
            toast.success(`Report generated for ${data.length} loans.`);
        } catch (error) {
            console.error("Error fetching loans report: ", error);
            toast.error("Failed to generate report.");
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedData = reportData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const totalAmount = reportData.reduce((sum, loan) => sum + (loan.amount || 0), 0);

    return (
        <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Takkevariheader />
            <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', color: '#1976d2', mb: 3 }}>
                    Amount Given Report
                </Typography>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="या तारखेपासून"
                            type="date"
                            name="startDate"
                            value={dates.startDate}
                            onChange={handleDateChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="या तारखेपर्यंत"
                            type="date"
                            name="endDate"
                            value={dates.endDate}
                            onChange={handleDateChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerateReport}
                            disabled={loading}
                            fullWidth
                            sx={{ height: '40px' }}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Generate Report'}
                        </Button>
                    </Grid>
                </Grid>

                {reportData.length > 0 && (
                    <>
                        <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell>Sr No</TableCell>
                                        <TableCell>खातेदारचे नाव   </TableCell>
                                        <TableCell>घेतलेली रक्कम (₹)</TableCell>
                                        <TableCell>घेतलेली दिनांक</TableCell>
                                        <TableCell>व्याज (%)</TableCell>
                                        <TableCell>महिना (Months)</TableCell>
                                        <TableCell>स्टेटस</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedData.map((loan, index) => (
                                        <TableRow key={loan.id}>
                                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                            <TableCell>{loan.customerName}</TableCell>
                                            <TableCell>{loan.amount?.toFixed(2) ?? '0.00'}</TableCell>
                                            <TableCell>{moment(loan.loandate.toDate()).format('DD-MM-YYYY')}</TableCell>
                                            <TableCell>{loan.interestRate}</TableCell>
                                            <TableCell>{loan.tenure}</TableCell>
                                            <TableCell>{loan.status}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
                                        <TableCell colSpan={2} align="right">
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>टोटल घेतलेली रक्कम :</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>₹{totalAmount.toFixed(2)}</Typography>
                                        </TableCell>
                                        <TableCell colSpan={4}></TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={reportData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}

                {!loading && reportData.length === 0 && dates.startDate && (
                    <Box sx={{ textAlign: 'center', py: 5 }}>
                        <Typography variant="h6" color="text.secondary">
                            No loan data found for the selected date range.
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default Amountgivenreport;